// Excel workbook integration module
import { logger } from "../../utils/logger";

/**
 * Gets Excel metadata (selected range and active worksheet name)
 * @returns Promise with object containing range (e.g., "A1:B11") and activeWorksheet (e.g., "Sales")
 */
export async function getExcelMetadata(): Promise<{ range: string; activeWorksheet: string }> {
    return new Promise((resolve, reject) => {
        if (typeof Excel === 'undefined') {
            reject(new Error("Excel API not available"));
            return;
        }

        Excel.run(async (context) => {
            try {
                // Get the selected range
                const selectedRange = context.workbook.getSelectedRange();
                
                // Get the active worksheet
                const worksheet = context.workbook.worksheets.getActiveWorksheet();
                
                // Load properties
                selectedRange.load(["address"]);
                worksheet.load(["name"]);
                await context.sync();
                
                // Extract range from address (e.g., "Sheet1!A1:B11" -> "A1:B11")
                let range = "";
                const address = selectedRange.address || "";
                if (address.includes('!')) {
                    // Extract the range part after the exclamation mark
                    range = address.split('!')[1];
                } else {
                    // If no exclamation mark, use the address as-is
                    range = address;
                }
                
                const activeWorksheet = worksheet.name || "";
                
                logger.debug(`[Excel] Metadata - Range: ${range}, ActiveWorksheet: ${activeWorksheet}`);
                resolve({ range, activeWorksheet });
            } catch (error) {
                logger.error("[Excel] Error getting metadata:", error);
                // Return empty values on error
                resolve({ range: "", activeWorksheet: "" });
            }
        });
    });
}

export async function getExcelWorkbook(): Promise<string> {
    return new Promise((resolve, reject) => {
        if (typeof Excel === 'undefined') {
            reject(new Error("Excel API not available"));
            return;
        }

        Excel.run(async (context) => {
            try {
                logger.debug(`[Excel] Starting Excel.run execution`);
                
                // Get the active worksheet
                const worksheet = context.workbook.worksheets.getActiveWorksheet();
                worksheet.load("name");
                await context.sync();
                
                logger.debug(`[Excel] Active worksheet: ${worksheet.name}`);
                
                // Get the used range (all cells with data)
                const usedRange = worksheet.getUsedRange();
                
                // Check if there's any data
                if (!usedRange) {
                    logger.debug(`[Excel] No used range found, worksheet is empty`);
                    resolve(`Worksheet: ${worksheet.name}\n\n(Empty worksheet)`);
                    return;
                }
                
                // Load the values from the used range
                usedRange.load("values");
                await context.sync();
                
                logger.debug(`[Excel] Used range loaded, rows: ${usedRange.values.length}, cols: ${usedRange.values[0]?.length || 0}`);
                
                // Format the data as a readable table
                const formattedData = formatExcelData(worksheet.name, usedRange.values);
                
                logger.debug(`[Excel] Excel content retrieved, length: ${formattedData.length}`);
                resolve(formattedData);
            } catch (error) {
                logger.error("[Excel] Fatal Error:", error);
                logger.error("[Excel] Error details:", {
                    errorMessage: error instanceof Error ? error.message : String(error),
                    errorStack: error instanceof Error ? error.stack : undefined,
                    errorName: error instanceof Error ? error.name : typeof error,
                });
                reject(error);
            }
        });
    });
}

/**
 * Formats Excel cell values as CSV format for API
 * @param worksheetName - Name of the worksheet
 * @param values - 2D array of cell values
 * @returns CSV formatted string representation of the data
 */
function formatExcelData(worksheetName: string, values: any[][]): string {
    if (!values || values.length === 0) {
        return `Worksheet: ${worksheetName}\n\n(Empty worksheet)`;
    }
    
    // Format each row as CSV
    const csvRows: string[] = [];
    
    for (let i = 0; i < values.length; i++) {
        const row = values[i];
        if (!row || row.length === 0) continue;
        
        // Format row with CSV escaping
        const formattedRow = row.map((cell: any) => {
            // Handle null, undefined, or empty cells
            if (cell === null || cell === undefined) {
                return "";
            }
            // Convert to string
            let cellValue = String(cell);
            
            // CSV escaping: if cell contains comma, quote, or newline, wrap in quotes and escape quotes
            if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n') || cellValue.includes('\r')) {
                // Escape quotes by doubling them
                cellValue = cellValue.replace(/"/g, '""');
                // Wrap in quotes
                cellValue = `"${cellValue}"`;
            }
            
            return cellValue;
        });
        
        // Join cells with comma separator
        csvRows.push(formattedRow.join(','));
    }
    
    // Combine with header text
    const csvContent = csvRows.join('\n');
    return `The current worksheet data is as follows (in CSV format):\n${csvContent}`;
}

/**
 * Inserts table data into Excel range
 * @param range - Excel range object
 * @param tableData - 2D array of cell values (array of arrays)
 */
function insertTable(range: Excel.Range, tableData: any[][]): void {
    // Excel requires a rectangular value array; normalize ragged/loose data first.
    const normalized = normalizeTable(tableData);
    if (normalized.length === 0) {
        logger.warn("[Excel] Invalid table data provided");
        return;
    }

    const rowCount = normalized.length;
    const columnCount = normalized[0].length;

    logger.debug(`[Excel] Inserting table with ${rowCount} rows and ${columnCount} columns`);

    // Get the top-left cell of the selected range and resize to match data dimensions
    const topLeftCell = range.getCell(0, 0);
    const targetRange = topLeftCell.getResizedRange(rowCount - 1, columnCount - 1);
    targetRange.values = normalized;
}

/**
 * Normalizes table data into a rectangular 2D array suitable for an Excel range.
 * Drops non-array rows, pads short rows to the widest column count, and coerces
 * cells Excel can't accept directly (null/undefined → "", objects → JSON string).
 * Returns [] when there is nothing usable to insert.
 * @param tableData - 2D array of cell values (possibly ragged)
 */
function normalizeTable(tableData: any[][]): any[][] {
    if (!Array.isArray(tableData) || tableData.length === 0) {
        return [];
    }

    const rows = tableData.filter((row) => Array.isArray(row));
    if (rows.length === 0) {
        return [];
    }

    const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0) || 1;

    return rows.map((row) => {
        const padded: any[] = [];
        for (let c = 0; c < columnCount; c++) {
            const cell = row[c];
            if (cell === null || cell === undefined) {
                padded.push("");
            } else if (typeof cell === "object") {
                padded.push(JSON.stringify(cell));
            } else {
                padded.push(cell);
            }
        }
        return padded;
    });
}

/**
 * Parses markdown table into 2D array
 * @param md - Markdown string containing table
 * @returns 2D array of table cells
 */
function parseMarkdownTable(md: string): string[][] {
    // 1. Split into lines and filter out empty lines or the separator row (---|---|---)
    const lines = md.trim().split('\n').filter(line => {
        const isSeparator = line.includes('---') && line.includes('|');
        return line.trim() !== "" && !isSeparator;
    });

    // 2. Map lines to arrays, cleaning up the pipe characters
    return lines.map(line => {
        // Remove leading/trailing pipes and split by pipe
        const cells = line.replace(/^\||\|$/g, '').split('|');
        // Trim whitespace and remove backticks (used for code formatting in markdown) from each cell
        return cells.map(cell => {
            let cleaned = cell.trim();
            // Remove leading and trailing backticks (markdown code formatting)
            cleaned = cleaned.replace(/^`+|`+$/g, '');
            return cleaned;
        });
    });
}

/**
 * Extracts markdown table from content
 * @param content - Content that may contain markdown tables
 * @returns 2D array of table cells or null if no table found
 */
function extractMarkdownTable(content: string): string[][] | null {
    const lines = content.split('\n');
    const tableLines: string[] = [];
    let inTable = false;
    let foundHeader = false;
    
    // Find consecutive lines that form a markdown table
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();
        const hasPipes = trimmedLine.includes('|');
        const isSeparator = trimmedLine.includes('---') && hasPipes;
        
        if (hasPipes) {
            // This is a table-related line (header, separator, or data row)
            if (!inTable) {
                // Start of a new table - clear any previous partial table
                tableLines.length = 0;
                foundHeader = false;
                inTable = true;
            }
            
            if (!isSeparator) {
                // This is a header or data row
                if (!foundHeader) {
                    foundHeader = true;
                }
                tableLines.push(line);
            } else {
                // Separator row - include it to maintain table structure, will be filtered out during parsing
                tableLines.push(line);
            }
        } else if (inTable) {
            // We were in a table but this line doesn't have pipes
            // If we have at least a header and one data row, we found a complete table
            if (foundHeader && tableLines.length >= 2) {
                break;
            }
            // Otherwise, reset and continue searching
            tableLines.length = 0;
            inTable = false;
            foundHeader = false;
        }
    }
    
    // Check if we found a valid table (at least header + 1 data row, or header + separator + data rows)
    // After filtering separator, we need at least 2 rows (header + at least one data row)
    if (tableLines.length >= 2) {
        const parsed = parseMarkdownTable(tableLines.join('\n'));
        // Ensure we have at least header and one data row after parsing
        if (parsed && parsed.length >= 2) {
            return parsed;
        }
    }
    
    return null;
}

/**
 * Inserts content into Excel workbook
 * @param content - Chat message content to insert (may contain markdown tables)
 * @returns Promise that resolves when insertion is complete
 */
export async function insertIntoExcel(
    content: string
): Promise<void> {
    if (typeof Excel === 'undefined') {
        throw new Error("Excel API not available");
    }

    return Excel.run(async (context) => {
        logger.debug(`[Excel] Starting insert operation`);
        
        // Get the selected range
        const range = context.workbook.getSelectedRange();
        
        logger.debug(`[Excel] Content to process, length: ${content.length}`);
        
        // Try to extract markdown table from content
        const tableData = extractMarkdownTable(content);
        
        // Insert data based on type
        if (tableData && Array.isArray(tableData) && tableData.length > 0 && Array.isArray(tableData[0])) {
            logger.debug(`[Excel] Found markdown table with ${tableData.length} rows and ${tableData[0].length} columns`);
            insertTable(range, tableData);
        } else {
            logger.debug(`[Excel] No markdown table found, inserting plain text`);
            // Insert as plain text in the selected cell - truncate if too long to prevent issues
            const textToInsert = content.length > 32767 ? content.substring(0, 32767) + "..." : content;
            range.values = [[textToInsert]];
        }
        
        await context.sync();
        logger.debug(`[Excel] Insert operation completed successfully`);
    });
}

/**
 * Returns the top-left (anchor) cell of a range address, e.g. "A1:B11" → "A1".
 * Strips any worksheet prefix ("Sheet1!A1" → "A1"). Returns "" for an empty address.
 * @param range - Range address from getExcelMetadata()
 */
export function startCellOf(range: string): string {
    if (!range) {
        return "";
    }
    const first = range.split(":")[0].trim();
    return first.includes("!") ? first.split("!")[1] : first;
}

/**
 * Strips a surrounding markdown code fence (```json ... ``` or ``` ... ```) if present.
 * The Excel insert prompt asks for raw JSON, but models occasionally wrap it anyway.
 */
function stripCodeFences(value: string): string {
    const trimmed = value.trim();
    const fenced = trimmed.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/i);
    return fenced ? fenced[1].trim() : trimmed;
}

/**
 * Returns value as a 2D array (array of arrays) if it already is one, else null.
 */
function coerceToTable(value: unknown): any[][] | null {
    if (Array.isArray(value) && value.length > 0 && value.every((row) => Array.isArray(row))) {
        return value as any[][];
    }
    return null;
}

/**
 * Parses the response from POST /api/v1/insert/excel into a 2D array of cells.
 *
 * That endpoint returns an InsertResponse envelope (`{ "content": "<json>" }`) whose
 * content is a JSON array-of-arrays (header row first) produced by the LLM. This
 * unwraps the envelope (up to two levels, since `content` may itself be a JSON-encoded
 * string), strips any stray markdown code fences, and coerces the result to a table.
 * Returns null if no usable table can be extracted — callers should then fall back.
 * @param raw - Raw response body text from formatExcelContent()
 */
export function parseBackendTableJson(raw: string): any[][] | null {
    if (!raw || typeof raw !== "string") {
        return null;
    }

    let text = stripCodeFences(raw);

    // Unwrap `{ content: ... }` envelopes up to two levels deep, coercing whatever
    // we land on to a table at each step.
    for (let depth = 0; depth < 3; depth++) {
        let parsed: unknown;
        try {
            parsed = JSON.parse(text);
        } catch {
            return null;
        }

        const table = coerceToTable(parsed);
        if (table) {
            return table;
        }

        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            const content = (parsed as { content?: unknown }).content;
            const innerTable = coerceToTable(content);
            if (innerTable) {
                return innerTable;
            }
            if (typeof content === "string") {
                text = stripCodeFences(content);
                continue;
            }
        }
        return null;
    }
    return null;
}

/**
 * Inserts a structured table (array of arrays, e.g. parsed from POST /api/v1/insert/excel)
 * into the currently selected Excel range, starting at its top-left cell. Cell strings
 * beginning with "=" are interpreted by Excel as formulas.
 * @param tableData - 2D array of cell values (header row first)
 */
export async function insertJsonTableIntoExcel(tableData: any[][]): Promise<void> {
    if (typeof Excel === "undefined") {
        throw new Error("Excel API not available");
    }

    const normalized = normalizeTable(tableData);
    if (normalized.length === 0) {
        throw new Error("No table data to insert");
    }

    return Excel.run(async (context) => {
        logger.debug(
            `[Excel] Inserting structured table: ${normalized.length} rows x ${normalized[0].length} cols`
        );
        const range = context.workbook.getSelectedRange();
        insertTable(range, normalized);
        await context.sync();
        logger.debug(`[Excel] Structured table insert completed`);
    });
}

